import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import config from "../../../lib/config";
import { siteApi } from "../../../lib/api";
import { launchGameById } from "../../../lib/gameLaunch";
import FavoriteToggleButton from "../../home/MainHome/FavoriteToggleButton";
import {
  createFavoriteGameItem,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import { getRecentPlayAccountKey } from "../../../utils/recentPlayHistory";
import { StyledSlotsPage } from "./StyledSlotsPage";
import RandomSlotPicker from "./RandomSlotPicker";

const PAGE_SIZE = 40;

const SORT_OPTIONS = [
  { value: "popular", label: "인기", orderByName: "sequence", orderByType: "ASC" },
  { value: "name", label: "이름순", orderByName: "game_name", orderByType: "ASC" },
];

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeKoreanTitle = (value = "") =>
  value
    .replace(/\bBTI\b/gi, "BTI")
    .replace(/\bSABA\b/gi, "SABA")
    .replace(/\s+/g, " ")
    .trim();

const getVendorTitle = (vendor) =>
  normalizeKoreanTitle(
    vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendor?.vendor_name ||
      "프로바이더"
  );

const isUsableSlotVendor = (vendor) =>
  typeof vendor?.vendor_name === "string" &&
  Boolean(vendor.vendor_name.trim()) &&
  vendor?.vendor_list != null;

const getGameTitle = (game) =>
  normalizeKoreanTitle(game?.game_name_ko || game?.game_name_kr || game?.name_ko || game?.game_name || "슬롯게임");

const getGameImage = (game) =>
  resolveAssetUrl(game?.game_image || game?.image || game?.thumbnail || game?.thumbnail_url);

const getSelectedVendorFromSearch = (search) => {
  const query = new URLSearchParams(search);
  return query.get("vendor") || "";
};

const getSortConfig = (sortBy) =>
  SORT_OPTIONS.find((option) => option.value === sortBy) || SORT_OPTIONS[0];

const dedupeGames = (games) => {
  const seen = new Set();

  return games.filter((game) => {
    const key = `${game?.game_id || game?.id || ""}:${game?.vendor || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const formatCount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "";
  return number.toLocaleString("ko-KR");
};

const ProviderCard = ({ vendor, isSelected, onSelect }) => {
  const title = getVendorTitle(vendor);

  return (
    <button
      type="button"
      className={`slot-provider-card${isSelected ? " is-selected" : ""}`}
      onClick={() => onSelect(vendor?.vendor_name || "")}
      aria-pressed={isSelected}
      title={title}
    >
      <span>{title}</span>
    </button>
  );
};

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "favorite" },
    })
  );
};

const SlotGameCard = ({
  game,
  vendorTitle,
  isFavorite,
  isLaunching,
  onFavoriteToggle,
  onLaunch,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const image = getGameImage(game);
  const title = getGameTitle(game);
  const playerCount =
    formatCount(game?.playing_count) ||
    formatCount(game?.online_count) ||
    formatCount(game?.current_players);

  return (
    <span className="slot-game-card-shell">
      <button
        type="button"
        className={`slot-game-card${isLaunching ? " is-launching" : ""}`}
        onClick={() => onLaunch(game, title)}
      >
        <span className="slot-game-image">
          {image && !imageFailed ? (
            <img src={image} alt={title} loading="lazy" onError={() => setImageFailed(true)} />
          ) : (
            <span className="slot-game-fallback">{title}</span>
          )}
          <span className="slot-game-overlay">
            <span className="slot-play-button" aria-hidden="true" />
            <strong>{isLaunching ? "실행 중" : title}</strong>
          </span>
          <span className="slot-game-title">{title}</span>
        </span>

        <span className="slot-game-meta">
          <span className="live-dot" />
          {playerCount ? `${playerCount} 플레이중` : vendorTitle || "플레이 가능"}
        </span>
      </button>
      <FavoriteToggleButton active={isFavorite} label={title} onToggle={onFavoriteToggle} />
    </span>
  );
};

const Slots = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const providerStripRef = useRef(null);
  const providerDragRef = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
    suppressClick: false,
  });
  const loadMoreRef = useRef(null);
  const requestRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(() =>
    getSelectedVendorFromSearch(location.search)
  );
  const [games, setGames] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [vendorLoading, setVendorLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [launchingGameId, setLaunchingGameId] = useState("");
  const [favoriteKeys, setFavoriteKeys] = useState(() => new Set());
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    setSelectedVendor(getSelectedVendorFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 280);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    let isMounted = true;

    const loadVendors = async () => {
      setVendorLoading(true);
      try {
        const response = await siteApi.getGameVendors({
          game_type: "slots",
          order_by_name: "sequence",
          order_by_type: "ASC",
          limit: "ALL",
        });

        if (!isMounted) return;
        setVendors(
          response.success ? getRows(response).filter(isUsableSlotVendor) : []
        );
      } finally {
        if (isMounted) setVendorLoading(false);
      }
    };

    loadVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!accountKey) {
      setFavoriteKeys(new Set());
      return undefined;
    }

    const refreshFavorites = () => {
      setFavoriteKeys(new Set(getFavoriteGames(accountKey).map((item) => item.key)));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey]);

  const vendorTitleByName = useMemo(() => {
    const map = new Map();
    vendors.forEach((vendor) => {
      if (vendor?.vendor_name) map.set(vendor.vendor_name, getVendorTitle(vendor));
    });
    return map;
  }, [vendors]);

  const loadGames = useCallback(
    async ({ pageToLoad = 1, append = false } = {}) => {
      if (append && loadingMoreRef.current) return;

      const requestId = ++requestRef.current;
      const sortConfig = getSortConfig(sortBy);

      if (append) {
        loadingMoreRef.current = true;
        setIsLoadingMore(true);
      } else {
        setIsInitialLoading(true);
        setError("");
      }

      try {
        const params = {
          page: pageToLoad,
          limit: PAGE_SIZE,
          game_type: "slots",
          order_by_name: sortConfig.orderByName,
          order_by_type: sortConfig.orderByType,
        };

        if (selectedVendor) params.vendor = selectedVendor;
        if (debouncedSearch) params.search = debouncedSearch;

        const response = await siteApi.getGames(params);
        if (requestId !== requestRef.current) return;

        if (!response.success) {
          if (!append) setGames([]);
          setError(response.error || "슬롯 게임을 불러오지 못했습니다.");
          return;
        }

        const rows = getRows(response);
        const next = response.data?.nextPage || null;
        const count = Number(response.data?.count || 0);

        setGames((current) => (append ? dedupeGames([...current, ...rows]) : rows));
        setPage(pageToLoad);
        setNextPage(next);
        setTotal(count);
      } catch {
        if (requestId !== requestRef.current) return;
        if (!append) setGames([]);
        setError("슬롯 게임을 불러오지 못했습니다.");
      } finally {
        if (requestId === requestRef.current || append) {
          if (append) {
            loadingMoreRef.current = false;
            setIsLoadingMore(false);
          } else {
            setIsInitialLoading(false);
          }
        }
      }
    },
    [debouncedSearch, selectedVendor, sortBy]
  );

  useEffect(() => {
    setGames([]);
    setPage(1);
    setNextPage(null);
    setTotal(0);
    loadGames({ pageToLoad: 1, append: false });
  }, [loadGames]);

  const hasMore = Boolean(nextPage) || (total > 0 && games.length < total);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isInitialLoading || isLoadingMore) return;
    loadGames({ pageToLoad: nextPage || page + 1, append: true });
  }, [hasMore, isInitialLoading, isLoadingMore, loadGames, nextPage, page]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || isInitialLoading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { rootMargin: "520px 0px" }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, isInitialLoading]);

  const handleVendorSelect = (vendorName) => {
    if (providerDragRef.current.suppressClick) return;

    const params = new URLSearchParams(location.search);
    if (vendorName) {
      params.set("vendor", vendorName);
    } else {
      params.delete("vendor");
    }

    const nextSearch = params.toString();
    navigate(`/slots${nextSearch ? `?${nextSearch}` : ""}`);
  };

  const handleProviderWheel = useCallback((event) => {
    const strip = providerStripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;

    const delta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;

    const maxScroll = strip.scrollWidth - strip.clientWidth;
    const nextScroll = Math.max(0, Math.min(maxScroll, strip.scrollLeft + delta));
    if (nextScroll === strip.scrollLeft) return;

    event.preventDefault();
    strip.scrollLeft = nextScroll;
  }, []);

  useEffect(() => {
    const strip = providerStripRef.current;
    if (!strip) return undefined;

    strip.addEventListener("wheel", handleProviderWheel, { passive: false });

    return () => {
      strip.removeEventListener("wheel", handleProviderWheel);
    };
  }, [handleProviderWheel]);

  const handleProviderPointerDown = useCallback((event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const strip = providerStripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;

    providerDragRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: strip.scrollLeft,
      moved: false,
      suppressClick: false,
    };
  }, []);

  const handleProviderPointerMove = useCallback((event) => {
    const drag = providerDragRef.current;
    const strip = providerStripRef.current;
    if (!drag.active || !strip) return;

    const distance = event.clientX - drag.startX;
    if (Math.abs(distance) < 4 && !drag.moved) return;

    drag.moved = true;
    drag.suppressClick = true;
    strip.classList.add("is-dragging");
    strip.scrollLeft = drag.scrollLeft - distance;
    event.preventDefault();
  }, []);

  const endProviderDrag = useCallback((event) => {
    const drag = providerDragRef.current;
    const strip = providerStripRef.current;
    if (!drag.active) return;

    drag.active = false;
    strip?.classList.remove("is-dragging");

    if (drag.moved) {
      window.setTimeout(() => {
        providerDragRef.current.suppressClick = false;
        providerDragRef.current.moved = false;
      }, 0);
    } else {
      drag.suppressClick = false;
    }
  }, []);

  const handleGameLaunch = async (game, title, metaOverride) => {
    const launchKey = `${game?.game_id || game?.id || ""}:${game?.vendor || ""}`;
    setLaunchingGameId(launchKey);
    try {
      return await launchGameById(game?.game_id, {
        label: title,
        recentPlay: {
          kind: "slot-game",
          gameType: "slots",
          gameId: game?.game_id || game?.id || "",
          vendorName: game?.vendor || game?.vendor_name || "",
          title,
          image: getGameImage(game),
          meta: metaOverride || selectedVendorTitle || "슬롯게임",
        },
      });
    } finally {
      setLaunchingGameId("");
    }
  };

  const handleFavoriteToggle = (game) => {
    if (!user?.isAuthenticated || !accountKey) {
      dispatchAuthRequired();
      return;
    }

    const favoriteItem = createFavoriteGameItem(game, {
      label: getGameTitle(game),
      gameType: "slots",
      meta: vendorTitleByName.get(game?.vendor) || "슬롯게임",
      image: getGameImage(game),
    });
    const result = toggleFavoriteGame(accountKey, favoriteItem);
    setFavoriteKeys(new Set(result.rows.map((row) => row.key)));
  };

  const selectedVendorTitle =
    vendors.find((vendor) => vendor?.vendor_name === selectedVendor)?.vendor_name_kr ||
    vendors.find((vendor) => vendor?.vendor_name === selectedVendor)?.vendor_name_ko ||
    selectedVendor;

  return (
    <StyledSlotsPage>
      <div className="slot-page-shell">
        <div className="slot-search-row">
          <label className="slot-search-box">
            <span className="search-icon" aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="슬롯 게임 검색"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        <div className="slot-toolbar">
          <div className="slot-toolbar-title">
            <span className="filter-icon" aria-hidden="true" />
            <strong>프로바이더</strong>
            {selectedVendor ? <em>{selectedVendorTitle}</em> : null}
          </div>

          <label className="slot-sort-control">
            <span>정렬</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div
          className="slot-provider-strip"
          aria-busy={vendorLoading}
          ref={providerStripRef}
          onPointerDown={handleProviderPointerDown}
          onPointerMove={handleProviderPointerMove}
          onPointerUp={endProviderDrag}
          onPointerCancel={endProviderDrag}
        >
          <button
            type="button"
            className={`slot-provider-card slot-provider-all${!selectedVendor ? " is-selected" : ""}`}
            onClick={() => handleVendorSelect("")}
            aria-pressed={!selectedVendor}
          >
            <span>전체</span>
          </button>

          {vendorLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                <span className="slot-provider-card provider-skeleton" key={`vendor-skeleton-${index}`} />
              ))
            : vendors.map((vendor) => (
                <ProviderCard
                  key={vendor?.id || vendor?.vendor_name}
                  vendor={vendor}
                  isSelected={selectedVendor === vendor?.vendor_name}
                  onSelect={handleVendorSelect}
                />
              ))}
        </div>

        <div className="slot-result-head">
          <div>
            <h1>슬롯게임</h1>
            <span>{total ? `${total.toLocaleString("ko-KR")}개 게임` : "실시간 슬롯 목록"}</span>
          </div>
          <div className="slot-result-actions">
            <RandomSlotPicker
              selectedVendor={selectedVendor}
              selectedVendorTitle={selectedVendorTitle || ""}
              activeVendors={vendors}
              warmupCandidates={games}
              launchingGameId={launchingGameId}
              onLaunchGame={handleGameLaunch}
            />
          </div>
        </div>

        {error ? <div className="slot-state error">{error}</div> : null}

        <div className="slot-game-grid" aria-busy={isInitialLoading}>
          {isInitialLoading
            ? Array.from({ length: 24 }).map((_, index) => (
                <span className="slot-game-skeleton" key={`slot-game-skeleton-${index}`} />
              ))
            : games.map((game) => {
                const launchKey = `${game?.game_id || game?.id || ""}:${game?.vendor || ""}`;

                return (
                  <SlotGameCard
                    key={`${game?.id || game?.game_id}-${game?.vendor}`}
                    game={game}
                    vendorTitle={vendorTitleByName.get(game?.vendor)}
                    isFavorite={favoriteKeys.has(
                      createFavoriteGameItem(game, {
                        label: getGameTitle(game),
                        gameType: "slots",
                        meta: vendorTitleByName.get(game?.vendor) || "슬롯게임",
                        image: getGameImage(game),
                      }).key
                    )}
                    isLaunching={launchingGameId === launchKey}
                    onFavoriteToggle={() => handleFavoriteToggle(game)}
                    onLaunch={handleGameLaunch}
                  />
                );
              })}
        </div>

        {!isInitialLoading && !error && games.length === 0 ? (
          <div className="slot-state">표시할 슬롯 게임이 없습니다.</div>
        ) : null}

        <div className="slot-load-more" ref={loadMoreRef}>
          {hasMore ? (
            <button type="button" onClick={handleLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? "불러오는 중" : "더 불러오기"}
            </button>
          ) : games.length > 0 ? (
            <span>모든 슬롯을 불러왔습니다.</span>
          ) : null}
        </div>
      </div>
    </StyledSlotsPage>
  );
};

export default Slots;
